"use client";

import { useState, useEffect, type MouseEvent } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Menu,
  Home,
  User,
  Code2,
  Briefcase,
  FolderKanban,
  Mail,
  ArrowRight,
  Github,
  Linkedin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
} from "@/components/ui/sheet";
import { motion } from "framer-motion";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import Logo from "@/public/logo.png";
import Image from "next/image";
import { usePortfolio } from "@/components/portfolio-provider";

const navLinks = [
  { name: "Home", href: "/", icon: <Home className="h-4 w-4" /> },
  { name: "About", href: "/about", icon: <User className="h-4 w-4" /> },
  { name: "Skills", href: "/skills", icon: <Code2 className="h-4 w-4" /> },
  {
    name: "Experience",
    href: "/experience",
    icon: <Briefcase className="h-4 w-4" />,
  },
  {
    name: "Projects",
    href: "/projects",
    icon: <FolderKanban className="h-4 w-4" />,
  },
  { name: "Contact", href: "/contact", icon: <Mail className="h-4 w-4" /> },
];

export default function Navbar() {
  const pathname = usePathname();
  const { data } = usePortfolio();
  const config = data?.siteConfig;
  const heroData = data?.hero;
  const contactEmail = heroData?.email || "sakibislamsami0808@gmail.com";
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToTop = (event?: MouseEvent<HTMLAnchorElement>) => {
    setIsMobileMenuOpen(false);

    if (typeof window === "undefined") return;

    if (pathname === "/") {
      event?.preventDefault();

      if (window.location.hash) {
        window.history.replaceState(null, "", "/");
      }

      const lenis = (window as any).__lenis;
      if (lenis && typeof lenis.scrollTo === "function") {
        lenis.scrollTo(0, { immediate: false, duration: 0.8 });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const handleNavClick = (
    event: MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    setIsMobileMenuOpen(false);

    if (href === pathname) {
      event.preventDefault();
      const lenis = (window as any).__lenis;
      if (lenis && typeof lenis.scrollTo === "function") {
        lenis.scrollTo(0, { immediate: false, duration: 0.8 });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        setScrolled(scrollY > 20);
        ticking = false;
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav className="fixed left-0 right-0 top-0 z-50 pt-3 md:pt-5 transition-all duration-300">
      <div className="container mx-auto px-4">
        <div
          className={`relative isolate flex items-center justify-between rounded-full px-4 py-2.5 transition-all duration-500 ${
            scrolled
              ? "liquid-dock shadow-[0_20px_50px_-10px_rgba(0,0,0,0.85),0_0_25px_rgba(201,168,76,0.12)] border border-white/20"
              : "bg-black/40 backdrop-blur-2xl border border-white/10 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.6)]"
          }`}
        >
          {/* Subtle Top Specular Gleam */}
          <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />

          {/* Logo */}
          <Link
            href="/"
            onClick={scrollToTop}
            className="group relative flex items-center gap-2 pl-2 pr-1 transition-transform active:scale-95"
          >
            <div className="relative">
              <Image
                src={config?.logoUrl || Logo}
                alt="Sakibul Islam"
                width={128}
                height={32}
                className="max-sm:w-24 w-32 object-contain transition-opacity duration-300 group-hover:opacity-90"
                priority
              />
            </div>
          </Link>

          {/* Desktop Apple Capsule Navigation Dock */}
          <div className="hidden lg:flex items-center gap-1 rounded-full bg-white/[0.04] p-1.5 border border-white/10 backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)]">
            {navLinks.map((link) => {
              const isActive =
                link.href === "/"
                  ? pathname === "/"
                  : pathname.startsWith(link.href);

              return (
                <Link
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  aria-current={isActive ? "page" : undefined}
                  className={`relative flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium tracking-wide transition-all duration-300 ${
                    isActive
                      ? "text-foreground font-semibold"
                      : "text-foreground/70 hover:text-foreground hover:bg-white/[0.05]"
                  }`}
                >
                  {/* Active Animated Capsule Pill */}
                  {isActive && (
                    <motion.div
                      layoutId="active-nav-dock"
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/25 via-primary/15 to-white/10 border border-primary/40 shadow-[0_0_15px_rgba(201,168,76,0.25),inset_0_1px_1px_rgba(255,255,255,0.3)]"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{link.icon}</span>
                  <span className="relative z-10">{link.name}</span>
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA Pill Button */}
          <div className="hidden lg:flex items-center gap-2">
            <Button
              asChild
              className="liquid-pill-btn rounded-full px-5 py-2 text-xs uppercase tracking-wider font-semibold text-black cursor-pointer"
            >
              <Link
                href="/contact"
                className="flex items-center gap-1.5"
              >
                <span>Let&apos;s Talk</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </Button>
          </div>

          {/* Mobile Navigation Toggle */}
          <div className="flex items-center gap-2 lg:hidden">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full h-9 w-9 bg-white/[0.05] border border-white/15 text-foreground hover:bg-white/[0.1] active:scale-95"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="flex flex-col w-full max-sm:w-full sm:max-w-sm border-l border-white/15 bg-gradient-to-b from-[#16110a] via-[#100d09] to-[#0a0805] backdrop-blur-3xl p-0 shadow-[0_0_50px_rgba(0,0,0,0.9),inset_1px_0_0_rgba(255,255,255,0.1)] overflow-hidden"
              >
                <VisuallyHidden.Root>
                  <SheetTitle>Navigation Menu</SheetTitle>
                </VisuallyHidden.Root>

                {/* Ambient Gold Mesh Orbs */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="absolute -right-16 top-0 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.18)_0%,transparent_70%)]" />
                  <div className="absolute -left-16 bottom-24 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.12)_0%,transparent_70%)]" />
                  <div className="absolute inset-0 bg-[repeating-linear-gradient(to_right,rgba(255,255,255,0.02)_0,rgba(255,255,255,0.02)_1px,transparent_1px,transparent_48px),repeating-linear-gradient(to_bottom,rgba(255,255,255,0.02)_0,rgba(255,255,255,0.02)_1px,transparent_1px,transparent_48px)] opacity-50" />
                </div>

                {/* Mobile Drawer Header */}
                <div className="relative z-10 px-6 pt-6 pb-4 border-b border-white/10 flex items-center justify-between">
                  <SheetClose asChild>
                    <Link
                      href="/"
                      onClick={scrollToTop}
                      className="inline-flex transition-transform active:scale-95"
                    >
                      <Image
                        src={config?.logoUrl || Logo}
                        alt="Logo"
                        width={130}
                        height={34}
                        className="w-32 object-contain"
                      />
                    </Link>
                  </SheetClose>
                </div>

                {/* Mobile Navigation Links */}
                <div className="relative z-10 flex flex-col flex-grow justify-start space-y-2 px-6 py-4 overflow-y-auto">
                  {navLinks.map((link, index) => {
                    const isActive =
                      link.href === "/"
                        ? pathname === "/"
                        : pathname.startsWith(link.href);

                    return (
                      <SheetClose asChild key={link.name}>
                        <Link
                          href={link.href}
                          onClick={(e) => handleNavClick(e, link.href)}
                          aria-current={isActive ? "page" : undefined}
                        >
                          <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.04, duration: 0.25 }}
                            className={`group relative flex items-center gap-3.5 rounded-2xl border px-3.5 py-3 text-sm font-medium transition-all duration-200 ${
                              isActive
                                ? "border-primary/40 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent text-foreground shadow-[inset_0_1px_1px_rgba(255,255,255,0.2),0_4px_20px_rgba(201,168,76,0.12)]"
                                : "border-white/[0.06] bg-white/[0.02] text-foreground/80 hover:border-white/15 hover:bg-white/[0.06] hover:text-foreground active:scale-[0.99]"
                            }`}
                          >
                            {/* Active Glowing Light Node */}
                            {isActive && (
                              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full bg-primary shadow-[0_0_8px_rgba(201,168,76,0.9)]" />
                            )}

                            <div
                              className={`flex h-8 w-8 items-center justify-center rounded-xl transition-all duration-200 ${
                                isActive
                                  ? "bg-primary/25 border border-primary/40 text-primary shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]"
                                  : "bg-white/[0.04] border border-white/10 text-foreground/70 group-hover:bg-primary/15 group-hover:text-primary group-hover:border-primary/30"
                              }`}
                            >
                              {link.icon}
                            </div>
                            <span className="flex-1 font-semibold tracking-wide">
                              {link.name}
                            </span>
                            <ArrowRight
                              className={`h-3.5 w-3.5 transition-transform duration-200 ${
                                isActive
                                  ? "text-primary translate-x-0 opacity-100"
                                  : "text-muted-foreground/50 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                              }`}
                            />
                          </motion.div>
                        </Link>
                      </SheetClose>
                    );
                  })}
                </div>

                {/* Quick Social & Connect Strip */}
                <div className="relative z-10 px-6 py-2.5">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5 backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.08)]">
                    <div className="flex items-center justify-between mb-3 px-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Social & Connect
                      </span>
                      <span className="h-1.5 w-1.5 rounded-full bg-primary/80 shadow-[0_0_6px_rgba(201,168,76,0.8)]" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <a
                        href={heroData?.githubUrl || "https://github.com/sakib-xrz"}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="GitHub Profile"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="group/item flex flex-col items-center justify-center gap-1.5 py-2 px-1 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-primary/40 hover:bg-primary/10 hover:shadow-[0_0_14px_rgba(201,168,76,0.18)] active:scale-95 transition-all duration-200"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04] border border-white/10 text-foreground/80 group-hover/item:border-primary/30 group-hover/item:bg-primary/20 group-hover/item:text-primary transition-colors">
                          <Github className="h-4 w-4" />
                        </div>
                        <span className="text-[10px] font-semibold text-foreground/75 group-hover/item:text-foreground">
                          GitHub
                        </span>
                      </a>
                      <a
                        href={heroData?.linkedinUrl || "https://linkedin.com/in/sakib0808"}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn Profile"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="group/item flex flex-col items-center justify-center gap-1.5 py-2 px-1 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-primary/40 hover:bg-primary/10 hover:shadow-[0_0_14px_rgba(201,168,76,0.18)] active:scale-95 transition-all duration-200"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04] border border-white/10 text-foreground/80 group-hover/item:border-primary/30 group-hover/item:bg-primary/20 group-hover/item:text-primary transition-colors">
                          <Linkedin className="h-4 w-4" />
                        </div>
                        <span className="text-[10px] font-semibold text-foreground/75 group-hover/item:text-foreground">
                          LinkedIn
                        </span>
                      </a>
                      <a
                        href={`mailto:${contactEmail}`}
                        aria-label="Send Email"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="group/item flex flex-col items-center justify-center gap-1.5 py-2 px-1 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:border-primary/40 hover:bg-primary/10 hover:shadow-[0_0_14px_rgba(201,168,76,0.18)] active:scale-95 transition-all duration-200"
                      >
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04] border border-white/10 text-foreground/80 group-hover/item:border-primary/30 group-hover/item:bg-primary/20 group-hover/item:text-primary transition-colors">
                          <Mail className="h-4 w-4" />
                        </div>
                        <span className="text-[10px] font-semibold text-foreground/75 group-hover/item:text-foreground">
                          Email
                        </span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Mobile Drawer Bottom Action */}
                <div className="relative z-10 mt-auto border-t border-white/10 p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] bg-gradient-to-t from-black/80 to-transparent">
                  <SheetClose asChild>
                    <Button
                      asChild
                      className="liquid-pill-btn h-12 w-full text-sm font-bold uppercase tracking-wider text-black rounded-full shadow-[0_4px_25px_rgba(201,168,76,0.3)] hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                    >
                      <Link
                        href="/contact"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center justify-center gap-2"
                      >
                        <span>Let&apos;s Talk</span>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
